import { useContext, useEffect, useRef } from "react";
import { UserContext } from "../pages/UserContext";
import {
  generateLoginUrl,
  getTokenExpiryMs,
  markReauthAttempt,
  readLastReauthAttemptMs,
  shouldReauthenticateOnExpiry,
} from "../utlities/auth.utility";
import {
  ACTIVITY_STORAGE_KEY,
  INACTIVITY_TIMEOUT_MS,
  SESSION_EXPIRY_REAUTH_LEAD_MS,
} from "../utlities/core.constants";

/**
 * Has the user interacted recently enough that they haven't already lapsed into the
 * inactivity-logout window? Reuses the shared last-activity timestamp maintained by
 * {@link useInactivityLogout}.
 */
function isUserActive(nowMs) {
  try {
    const raw = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const lastActivityMs = Number(raw);
    if (!Number.isFinite(lastActivityMs)) {
      return false;
    }
    return nowMs - lastActivityMs < INACTIVITY_TIMEOUT_MS;
  } catch {
    return false;
  }
}

/**
 * Proactively keeps an ACTIVE user signed in across Login.gov session-token expiry.
 *
 * Unlike inactivity logout (which ends the session of a user who *stops* interacting), the
 * Login.gov `id_token` has a hard lifetime that expires even for a continuously-active user — that
 * is the case this handles. Because Login.gov issues no refresh token, the only way to renew is to
 * re-run the authorize redirect; with an active SSO session that returns immediately to the same
 * page (return path travels in the OIDC `state`), and in-progress work persists in localStorage.
 *
 * Behavior:
 * - Shortly before the token's `exp` (by {@link SESSION_EXPIRY_REAUTH_LEAD_MS}), if the user is
 *   still active, silently re-authenticate. No prompt — an actively-working user clearly wants to
 *   stay signed in.
 * - If the user is idle, do nothing: the inactivity-logout flow intentionally owns that case.
 * - If the token is already expired (or already inside the lead window at mount), do nothing here
 *   and let the response interceptor's reactive re-auth handle the next request. This also avoids a
 *   redirect loop for pathologically short token lifetimes.
 *
 * Shares the same anti-loop guard ({@link shouldReauthenticateOnExpiry}) as the reactive path.
 */
export default function useSessionExpiry() {
  const { token } = useContext(UserContext);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!token) {
      return undefined;
    }

    const expiryMs = getTokenExpiryMs(token);
    if (!expiryMs) {
      return undefined;
    }

    const msUntilReauth = expiryMs - SESSION_EXPIRY_REAUTH_LEAD_MS - Date.now();
    if (msUntilReauth <= 0) {
      // Already expired or already inside the lead window; defer to the reactive interceptor.
      return undefined;
    }

    timerRef.current = setTimeout(() => {
      const nowMs = Date.now();

      // Keep only active users signed in automatically; idle users fall to inactivity logout.
      if (!isUserActive(nowMs)) {
        return;
      }
      // Respect the shared anti-loop window (e.g. a token repeatedly rejected server-side).
      if (!shouldReauthenticateOnExpiry(nowMs, readLastReauthAttemptMs())) {
        return;
      }
      if (typeof window === "undefined") {
        return;
      }

      markReauthAttempt(nowMs);
      window.location.assign(generateLoginUrl());
    }, msUntilReauth);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [token]);
}
